/**
 * PROTECTED FILE — DO NOT MODIFY WITHOUT PERMISSION
 *
 * Last verified working: 2026-01-03
 *
 * This file controls: flight list display and status updates
 * Changes here have historically broken core game function.
 *
 * Before modifying:
 * 1. Get explicit approval from Zach
 * 2. Create a checkpoint commit
 * 3. Test full baseline after ANY change
 */

// RoutesPanel.jsx - Simple mode route management
// One-click operations, auto-dispatch, route cards with status

import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { debug } from "../../lib/debug.js";
import { useWorldState } from "../state/WorldStateContext.jsx";
import { useActiveFlights, useRecentCancellations, calculateFlightTime, calculateEarnings, AIRPORTS } from "../sim/GameLoopEngine.jsx";
import { GLOBAL_AIRPORTS } from "../../data/GlobalAirports.js";
import { AIRPORTS as FULL_AIRPORTS } from "../../systems/AirportDatabase.js";
import { AIRCRAFT_DATABASE } from "../../data/aircraft.js";
import { calculateEarningsSafe } from "../../systems/EarningsEngine.js";
import { calculateFuelNeeded, checkFuelAvailability } from "../../systems/FuelSystem.js";
import { processAnomalousBehavior, isAircraftGrounded, getGroundedDaysRemaining, hasAnomalousBehavior } from "../../systems/AnomalousBehaviors.js";
import { getYieldMultiplier, getRouteColor, getRouteDemand } from "../../systems/RouteDemandSystem.js";
import {
  getWingsState,
  findStaleRoutes,
  canAffordRouteGeneration,
  WINGS_CONFIG,
} from "../../systems/WingsSystem.js";
import { toast } from "@/components/ui/use-toast.jsx";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import AnomalyModal from "../modals/AnomalyModal.jsx";
import TheCodeMinigame from "../TheCodeMinigame.jsx";
import WingsSpendModal from "../ui/WingsSpendModal.jsx";
// Icons removed - text-only UI

// Route status colors and labels
const STATUS_CONFIG = {
  unassigned: { color: "#737373", abbrev: "--", label: "Unassigned", bgColor: "#1a1a1a" },
  ready: { color: "#22c55e", abbrev: "OK", label: "Ready", bgColor: "#052e16" },
  in_flight: { color: "#3b82f6", abbrev: "FLY", label: "In Flight", bgColor: "#0c1929" },
  landed: { color: "#60ffd0", abbrev: "LND", label: "Landed", bgColor: "#0d1f1a" },  // 3-sec landing beat
  turnaround: { color: "#f59e0b", abbrev: "TRN", label: "Turnaround", bgColor: "#1c1302" },
  delayed: { color: "#ef4444", abbrev: "DLY", label: "Delayed", bgColor: "#1f0a0a" },
  crew_issue: { color: "#ef4444", abbrev: "CRW", label: "Crew Issue", bgColor: "#1f0a0a" },
};

// Accordion section definitions - order matters for display
// labelKey is the i18n key in routes namespace
const ACCORDION_SECTIONS = [
  { key: 'ready', labelKey: 'readyToLaunch', color: '#22c55e' },
  { key: 'in_flight', labelKey: 'inFlight', color: '#3b82f6' },
  { key: 'unassigned', labelKey: 'unassigned', color: '#737373' },
];

// Compressed ground ops phases for Simple mode (15 min total vs 30 min in Simulation)
// Creates OTP pressure - player must dispatch before scheduled time or accumulate delay
// Ground ops phases - scaled for ~15 real seconds at 144x clock rate
// Total: 36 game minutes = 15 real seconds
const SIMPLE_GROUND_PHASES = {
  preboard: { name: "Pre-board", abbrev: "PRE", color: "#3b82f6", startMinutes: 36 },
  boarding: { name: "Boarding", abbrev: "BRD", color: "#eab308", startMinutes: 26 },
  doorclose: { name: "Door Close", abbrev: "CLO", color: "#ef4444", startMinutes: 12 },
  taxi: { name: "Taxi", abbrev: "TAX", color: "#22c55e", startMinutes: 5 },
};
const PHASE_ORDER = ["preboard", "boarding", "doorclose", "taxi"];
const AUTO_CANCEL_MINUTES = 45; // Cancel if not dispatched by T+45

// Format currency
const formatMoney = (amount) => {
  if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
  return `$${amount}`;
};

// Local distance calculation with GLOBAL_AIRPORTS fallback for manual routes
// This allows routes between airports not in AIRPORTS/FULL_AIRPORTS (~400) to launch
const calculateDistanceWithGlobal = (originCode, destCode) => {
  const origin = AIRPORTS[originCode] || FULL_AIRPORTS[originCode] || GLOBAL_AIRPORTS[originCode];
  const dest = AIRPORTS[destCode] || FULL_AIRPORTS[destCode] || GLOBAL_AIRPORTS[destCode];

  if (!origin || !dest || !origin.lat || !dest.lat) {
    console.warn('[RoutesPanel] Airport not found for distance calc:', { originCode, destCode, originFound: !!origin, destFound: !!dest });
    return NaN;
  }

  const R = 3440.065; // Earth radius in nautical miles
  const lat1 = origin.lat * Math.PI / 180;
  const lat2 = dest.lat * Math.PI / 180;
  const dLat = (dest.lat - origin.lat) * Math.PI / 180;
  const dLon = (dest.lon - origin.lon) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1) * Math.cos(lat2) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return Math.round(R * c);
};

// Ground Ops Progress Bar - Shows countdown phases for scheduled departures
// Creates OTP pressure: if player dispatches late, delay accumulates
function GroundOpsProgressBar({ scheduledDeparture, gameTime }) {
  const now = gameTime ? new Date(gameTime) : new Date();
  const scheduledDep = new Date(scheduledDeparture);
  const minutesUntilDeparture = (scheduledDep - now) / 60000;

  // Determine current phase based on time until scheduled departure
  const currentPhaseInfo = useMemo(() => {
    for (let i = 0; i < PHASE_ORDER.length; i++) {
      const phaseKey = PHASE_ORDER[i];
      const config = SIMPLE_GROUND_PHASES[phaseKey];
      const nextPhaseKey = PHASE_ORDER[i + 1];
      const nextConfig = nextPhaseKey ? SIMPLE_GROUND_PHASES[nextPhaseKey] : null;

      const phaseStart = config.startMinutes;
      const phaseEnd = nextConfig ? nextConfig.startMinutes : 0;

      if (minutesUntilDeparture <= phaseStart && minutesUntilDeparture > phaseEnd) {
        const phaseDuration = phaseStart - phaseEnd;
        const elapsed = phaseStart - minutesUntilDeparture;
        return {
          phaseKey,
          config,
          progress: Math.min(1, elapsed / phaseDuration),
          phaseIndex: i,
        };
      }
    }
    // Past departure time - all phases complete
    if (minutesUntilDeparture <= 0) {
      return {
        phaseKey: "taxi",
        config: SIMPLE_GROUND_PHASES.taxi,
        progress: 1,
        phaseIndex: PHASE_ORDER.length - 1,
        pastDeparture: true,
      };
    }
    // Before ground ops start (>36 game min out)
    return null;
  }, [minutesUntilDeparture]);

  // Not yet in ground ops window (starts at T-36 game min)
  if (!currentPhaseInfo && minutesUntilDeparture > 36) {
    const minsToStart = Math.ceil(minutesUntilDeparture - 36);
    return (
      <div className="text-xs text-center py-1" style={{ color: "#ef4444" }}>
        Boarding in {minsToStart}m
      </div>
    );
  }

  // Calculate delay if past scheduled departure
  const delayMinutes = minutesUntilDeparture < 0 ? Math.abs(Math.floor(minutesUntilDeparture)) : 0;
  const isDelayed = delayMinutes > 0;

  return (
    <div className="py-1.5">
      {/* Phase indicator bar */}
      <div className="flex gap-0.5 mb-1">
        {PHASE_ORDER.map((phaseKey, index) => {
          const config = SIMPLE_GROUND_PHASES[phaseKey];
          const isCompleted = currentPhaseInfo && index < currentPhaseInfo.phaseIndex;
          const isActive = currentPhaseInfo && index === currentPhaseInfo.phaseIndex;

          return (
            <div
              key={phaseKey}
              className="flex-1 h-3 rounded-sm relative overflow-hidden"
              style={{ backgroundColor: "#1a1a1a" }}
            >
              {/* Completed or active fill */}
              <div
                className="absolute inset-0 h-full transition-all duration-500"
                style={{
                  backgroundColor: config.color,
                  width: isCompleted ? "100%" : isActive ? `${currentPhaseInfo.progress * 100}%` : "0%",
                  opacity: isCompleted ? 0.6 : 1,
                }}
              />
              {/* Phase label */}
              <span
                className="absolute inset-0 flex items-center justify-center text-[9px] font-bold"
                style={{ color: isCompleted || isActive ? "#000" : "#404040" }}
              >
                {config.abbrev}
              </span>
            </div>
          );
        })}
      </div>

      {/* Time display */}
      <div className="flex justify-between text-xs">
        {isDelayed ? (
          <span className="font-bold" style={{ color: "#ef4444" }}>LATE +{delayMinutes}m</span>
        ) : (
          <span style={{ color: "#22c55e" }}>T-{Math.ceil(minutesUntilDeparture)}m</span>
        )}
        <span style={{ color: "#525252", fontSize: "10px" }}>
          {scheduledDep.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
        </span>
      </div>
    </div>
  );
}

// Collapsible Accordion Section for route grouping
function AccordionSection({
  label,
  color,
  count,
  isExpanded,
  onToggle,
  children
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg overflow-hidden" style={{ border: `1px solid ${color}40` }}>
      {/* Header - clickable to expand/collapse */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 transition-colors"
        style={{
          backgroundColor: isExpanded ? `${color}15` : '#0c0c0c',
          borderBottom: isExpanded ? `1px solid ${color}30` : 'none',
        }}
      >
        <div className="flex items-center gap-3">
          {/* Expand/collapse indicator */}
          <span
            className="text-sm font-mono transition-transform duration-200"
            style={{
              color: color,
              transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
              display: 'inline-block',
            }}
          >
            &gt;
          </span>
          {/* Section label */}
          <span className="font-bold" style={{ color: color }}>
            {label}
          </span>
          {/* Count badge */}
          <span
            className="px-2 py-0.5 rounded text-xs font-bold"
            style={{
              backgroundColor: `${color}20`,
              color: color,
            }}
          >
            {count}
          </span>
        </div>

      </button>

      {/* Content - route cards grid */}
      {isExpanded && count > 0 && (
        <div className="p-3" style={{ backgroundColor: '#0a0a0a' }}>
          {children}
        </div>
      )}

      {/* Empty state when expanded but no routes */}
      {isExpanded && count === 0 && (
        <div
          className="p-4 text-center text-sm"
          style={{ backgroundColor: '#0a0a0a', color: '#525252' }}
        >
          {t('routes.noRoutesCategory')}
        </div>
      )}
    </div>
  );
}

// Compact Route Card Component - Grid layout for high density
function RouteCard({ route, aircraft, activeFlights, recentCancellations = [], onLaunch, onAssignAircraft, onTrack, onSchedule, onDelete, onUnassign, gameTime, routeDemand }) {
  // ROUND-TRIP: Use current leg direction for display
  const legOrigin = route.currentLegOrigin || route.origin;
  const legDestination = route.currentLegDestination || route.destination;
  const originAirport = GLOBAL_AIRPORTS[legOrigin] || { name: legOrigin, city: "" };
  const destAirport = GLOBAL_AIRPORTS[legDestination] || { name: legDestination, city: "" };

  // Calculate route info
  const distance = useMemo(() => {
    if (!originAirport.lat || !destAirport.lat) return 0;
    const R = 3440.065;
    const lat1 = originAirport.lat * Math.PI / 180;
    const lat2 = destAirport.lat * Math.PI / 180;
    const dLat = (destAirport.lat - originAirport.lat) * Math.PI / 180;
    const dLon = (destAirport.lon - originAirport.lon) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1) * Math.cos(lat2) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return Math.round(R * c);
  }, [originAirport, destAirport]);

  // Get assigned aircraft info
  const assignedAircraft = aircraft?.find(a => a.id === route.aircraftId);

  // DEBUG: Log aircraft assignment state
  debug('[RouteCard DEBUG]', legOrigin, '→', legDestination, {
    routeId: route.id,
    routeAircraftId: route.aircraftId,
    isBidirectional: route.isBidirectional,
    currentLeg: `${legOrigin}→${legDestination}`,
    assignedAircraftFound: !!assignedAircraft,
    assignedAircraftReg: assignedAircraft?.registration,
    assignedAircraftStatus: assignedAircraft?.status,
    routeStatus: route.status,
  });

  // Check if THIS ROUTE'S CURRENT AIRCRAFT is on a flight
  // Must match aircraftId to handle reassignment: if route's aircraft changed, old flight shouldn't count
  // For round-trip routes, check both directions (outbound and return leg)
  const currentFlight = activeFlights.find(f =>
    f.aircraftId === route.aircraftId && (
      f.routeId === route.id ||
      (f.origin === route.origin && f.destination === route.destination) ||
      (f.origin === route.destination && f.destination === route.origin)  // Return leg
    )
  );

  // Determine actual status based on flight state
  // IMPORTANT: Check for active flight FIRST - flight state takes priority over route.aircraftId
  const actualStatus = useMemo(() => {
    // If there's an active flight on this route, show flight status regardless of aircraftId
    if (currentFlight) {
      const progress = currentFlight.distanceFraction ?? currentFlight.progress ?? 0;
      const phase = currentFlight.phase || '';
      const status = currentFlight.status || '';

      // Only consider done when explicitly marked as arrived/completed/landed
      // Do NOT use progress >= 0.99 - this causes blinking as flights near completion
      const isDone =
        phase === 'arrived' || status === 'arrived' ||
        phase === 'completed' || status === 'completed' ||
        phase === 'landed' || status === 'landed' ||
        currentFlight.awaitingVisualArrival === true;

      if (isDone) {
        return 'ready';
      }
      if (phase === 'turnaround' || status === 'turnaround') return 'turnaround';
      return 'in_flight';
    }
    // No active flight - check if aircraft is assigned
    if (!route.aircraftId) return 'unassigned';
    if (assignedAircraft?.status === 'turnaround') return 'turnaround';
    if (assignedAircraft?.status === 'available' || assignedAircraft?.status === 'ready') return 'ready';
    return route.status || 'ready';
  }, [route, currentFlight, assignedAircraft]);

  const statusConfig = STATUS_CONFIG[actualStatus] || STATUS_CONFIG.unassigned;

  // Check if this route had a recent cancellation
  const recentCancellation = recentCancellations.find(f =>
    f.routeId === route.id ||
    (f.origin === route.origin && f.destination === route.destination) ||
    (f.origin === route.destination && f.destination === route.origin) // Return leg
  );

  // Estimate revenue (with demand multiplier)
  const estimatedRevenue = useMemo(() => {
    if (!assignedAircraft) return 0;
    const pax = Math.floor((assignedAircraft.capacity || 76) * 0.85);
    const ticketPrice = 10 * distance;
    let revenue = Math.round(pax * ticketPrice);

    // Apply route demand multiplier (uses current leg direction)
    if (routeDemand) {
      const demandMultiplier = getYieldMultiplier(legOrigin, legDestination, routeDemand);
      revenue = Math.round(revenue * demandMultiplier);
    }

    return revenue;
  }, [assignedAircraft, distance, routeDemand, legOrigin, legDestination]);

  // Time remaining for in-flight
  const timeRemaining = useMemo(() => {
    if (!currentFlight || actualStatus !== 'in_flight') return null;
    const progress = currentFlight.distanceFraction || currentFlight.progress || 0;
    const totalMinutes = calculateFlightTime(distance, assignedAircraft?.type) * 60;
    const remaining = Math.round(totalMinutes * (1 - progress));
    if (remaining > 60) return `${Math.floor(remaining / 60)}h${remaining % 60}m`;
    return `${remaining}m`;
  }, [currentFlight, actualStatus, distance, assignedAircraft]);

  return (
    <div
      className={`rounded-lg p-3 flex flex-col ${recentCancellation ? 'animate-pulse' : ''}`}
      style={{
        backgroundColor: recentCancellation ? "#2a0a0a" : statusConfig.bgColor,
        border: recentCancellation ? "2px solid #ef4444" : `1px solid ${statusConfig.color}40`,
        minHeight: "140px",
        boxShadow: recentCancellation ? "0 0 20px rgba(239, 68, 68, 0.4)" : "none",
      }}
    >
      {/* CANCELLATION BANNER - Full width alert */}
      {recentCancellation && (
        <div
          className="mb-2 px-2 py-1.5 rounded text-center"
          style={{
            backgroundColor: "#ef4444",
            color: "#fff",
            fontWeight: "bold",
            fontSize: "12px",
            letterSpacing: "1px",
          }}
        >
          FLIGHT CANCELLED - {recentCancellation.cancelReason || 'No dispatch within window'}
        </div>
      )}

      {/* Row 1: Route + Status + Delete */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1">
          <span className="text-base font-bold" style={{ color: "#fff" }}>
            {legOrigin}
          </span>
          <span className="text-xs" style={{ color: statusConfig.color }}>→</span>
          <span className="text-base font-bold" style={{ color: "#fff" }}>
            {legDestination}
          </span>
          {/* Show bidirectional indicator for round-trip routes */}
          {route.isBidirectional && (
            <span className="text-xs ml-1" style={{ color: "#525252" }}>↺</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <span
            className="text-xs px-1.5 py-0.5 rounded"
            style={{ backgroundColor: statusConfig.color + "20", color: statusConfig.color }}
          >
            {statusConfig.abbrev}
          </span>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(route.id); }}
            className="text-xs px-1 hover:bg-red-900/50 rounded"
            title="Delete Route"
            style={{ color: "#ef4444" }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Row 2: Cities + Distance */}
      <div className="text-xs truncate mb-2" style={{ color: "#737373" }}>
        {originAirport.city?.split(',')[0] || legOrigin} → {destAirport.city?.split(',')[0] || legDestination} • {distance}nm
      </div>

      {/* Row 3: Aircraft info OR assign button */}
      <div className="flex-1">
        {assignedAircraft ? (
          <div className="text-xs space-y-1">
            <div className="flex items-center justify-between">
              <span>
                <span className="font-mono" style={{ color: "#22d3ee" }}>{assignedAircraft.registration}</span>
                <span className="ml-1" style={{ color: "#525252" }}>{assignedAircraft.model || assignedAircraft.type}</span>
              </span>
              {actualStatus !== 'in_flight' && (
                <button
                  onClick={(e) => { e.stopPropagation(); onUnassign(route.id); }}
                  className="hover:bg-orange-900/50 rounded px-1"
                  title="Unassign"
                  style={{ color: "#f59e0b" }}
                >
                  ✕
                </button>
              )}
            </div>
            <div className="flex items-center justify-between">
              <span style={{ color: "#525252" }}>{assignedAircraft.capacity || 76} pax</span>
              {estimatedRevenue > 0 && (
                <span className="font-bold" style={{ color: "#22c55e" }}>{formatMoney(estimatedRevenue)}</span>
              )}
            </div>
            {timeRemaining && (
              <div style={{ color: "#3b82f6" }}>{timeRemaining} left</div>
            )}
          </div>
        ) : (
          <button
            onClick={() => onAssignAircraft(route.id)}
            className="w-full text-xs py-1 rounded"
            style={{ backgroundColor: "#1a1a1a", color: "#737373", border: "1px dashed #404040" }}
          >
            + Assign
          </button>
        )}
      </div>

      {/* Ground Ops Progress Bar - shows when aircraft assigned with scheduled departure */}
      {assignedAircraft && route.scheduledDeparture && actualStatus === 'ready' && (
        <GroundOpsProgressBar
          scheduledDeparture={route.scheduledDeparture}
          gameTime={gameTime}
        />
      )}

      {/* Row 4: Action buttons */}
      <div className="mt-2 pt-2" style={{ borderTop: "1px solid #333" }}>
        {actualStatus === 'ready' && (
          <div className="flex gap-1">
            <button
              onClick={() => onLaunch(route)}
              className="flex-1 text-xs py-1.5 rounded font-bold"
              style={{ backgroundColor: "#22c55e", color: "#000" }}
            >
              DEPART
            </button>
            {onSchedule && (
              <button
                onClick={() => onSchedule(route)}
                className="text-xs py-1.5 px-2 rounded"
                style={{ backgroundColor: "#8b5cf620", color: "#8b5cf6", border: "1px solid #8b5cf6" }}
              >
                SCHED
              </button>
            )}
          </div>
        )}
        {actualStatus === 'in_flight' && currentFlight && (
          <button
            onClick={() => onTrack(currentFlight)}
            className="w-full text-xs py-1.5 rounded"
            style={{ backgroundColor: "#3b82f620", color: "#3b82f6", border: "1px solid #3b82f6" }}
          >
            TRACK
          </button>
        )}
        {actualStatus === 'turnaround' && (
          <div className="text-xs text-center py-1" style={{ color: "#f59e0b" }}>Turnaround...</div>
        )}
        {actualStatus === 'unassigned' && (
          <div className="text-xs text-center py-1" style={{ color: "#525252" }}>No aircraft</div>
        )}
      </div>
    </div>
  );
}

// Compact List View Row - Single row per route for dense display
function RouteListItem({ route, aircraft, activeFlights, recentCancellations = [], onLaunch, onAssignAircraft, onTrack, onSchedule, onDelete, onUnassign, gameTime, routeDemand }) {
  // ROUND-TRIP: Use current leg direction for display
  const legOrigin = route.currentLegOrigin || route.origin;
  const legDestination = route.currentLegDestination || route.destination;
  const originAirport = GLOBAL_AIRPORTS[legOrigin] || { name: legOrigin, city: "" };
  const destAirport = GLOBAL_AIRPORTS[legDestination] || { name: legDestination, city: "" };

  // Calculate distance
  const distance = useMemo(() => {
    if (!originAirport.lat || !destAirport.lat) return 0;
    const R = 3440.065;
    const lat1 = originAirport.lat * Math.PI / 180;
    const lat2 = destAirport.lat * Math.PI / 180;
    const dLat = (destAirport.lat - originAirport.lat) * Math.PI / 180;
    const dLon = (destAirport.lon - originAirport.lon) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1) * Math.cos(lat2) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return Math.round(R * c);
  }, [originAirport, destAirport]);

  // Get assigned aircraft
  const assignedAircraft = aircraft?.find(a => a.id === route.aircraftId);

  // Check for active flight (both directions for round-trip routes)
  const currentFlight = activeFlights.find(f =>
    f.aircraftId === route.aircraftId && (
      f.routeId === route.id ||
      (f.origin === route.origin && f.destination === route.destination) ||
      (f.origin === route.destination && f.destination === route.origin)  // Return leg
    )
  );

  // Determine status (same logic as RouteCard)
  const actualStatus = useMemo(() => {
    if (currentFlight) {
      const progress = currentFlight.distanceFraction ?? currentFlight.progress ?? 0;
      const phase = currentFlight.phase || '';
      const status = currentFlight.status || '';
      // Only consider done when explicitly marked as arrived/completed/landed
      // Do NOT use progress >= 0.99 - this causes blinking as flights near completion
      const isDone =
        phase === 'arrived' || status === 'arrived' ||
        phase === 'completed' || status === 'completed' ||
        phase === 'landed' || status === 'landed' ||
        currentFlight.awaitingVisualArrival === true;
      if (isDone) return 'ready';
      if (phase === 'turnaround' || status === 'turnaround') return 'turnaround';
      return 'in_flight';
    }
    if (!route.aircraftId) return 'unassigned';
    if (assignedAircraft?.status === 'turnaround') return 'turnaround';
    if (assignedAircraft?.status === 'available' || assignedAircraft?.status === 'ready') return 'ready';
    return route.status || 'ready';
  }, [route, currentFlight, assignedAircraft]);

  const statusConfig = STATUS_CONFIG[actualStatus] || STATUS_CONFIG.unassigned;

  // Check if this route had a recent cancellation
  const recentCancellation = recentCancellations.find(f =>
    f.routeId === route.id ||
    (f.origin === route.origin && f.destination === route.destination) ||
    (f.origin === route.destination && f.destination === route.origin) // Return leg
  );

  // Revenue estimate (with demand multiplier)
  const estimatedRevenue = useMemo(() => {
    if (!assignedAircraft) return 0;
    const pax = Math.floor((assignedAircraft.capacity || 76) * 0.85);
    let revenue = Math.round(pax * 10 * distance);

    // Apply route demand multiplier (uses current leg direction)
    if (routeDemand) {
      const demandMultiplier = getYieldMultiplier(legOrigin, legDestination, routeDemand);
      revenue = Math.round(revenue * demandMultiplier);
    }

    return revenue;
  }, [assignedAircraft, distance, routeDemand, legOrigin, legDestination]);

  // Time remaining for in-flight
  const timeRemaining = useMemo(() => {
    if (!currentFlight || actualStatus !== 'in_flight') return null;
    const progress = currentFlight.distanceFraction || currentFlight.progress || 0;
    const totalMinutes = calculateFlightTime(distance, assignedAircraft?.type) * 60;
    const remaining = Math.round(totalMinutes * (1 - progress));
    if (remaining > 60) return `${Math.floor(remaining / 60)}h${remaining % 60}m`;
    return `${remaining}m`;
  }, [currentFlight, actualStatus, distance, assignedAircraft]);

  return (
    <div
      className={`flex items-center gap-3 px-3 py-2 rounded ${recentCancellation ? 'animate-pulse' : ''}`}
      style={{
        backgroundColor: recentCancellation ? "#2a0a0a" : statusConfig.bgColor,
        border: recentCancellation ? "2px solid #ef4444" : `1px solid ${statusConfig.color}30`,
        boxShadow: recentCancellation ? "0 0 15px rgba(239, 68, 68, 0.3)" : "none",
      }}
    >
      {/* CANCELLATION BADGE - Prominent */}
      {recentCancellation ? (
        <span
          className="text-[10px] font-bold px-2 py-1 rounded"
          style={{ backgroundColor: "#ef4444", color: "#fff", letterSpacing: "0.5px" }}
        >
          CANCELLED
        </span>
      ) : (
        <span
          className="text-[10px] font-bold px-1.5 py-0.5 rounded"
          style={{ backgroundColor: statusConfig.color + "20", color: statusConfig.color }}
        >
          {statusConfig.abbrev}
        </span>
      )}

      {/* Route - shows current leg direction */}
      <div className="flex items-center gap-1 min-w-[100px]">
        <span className="font-bold" style={{ color: "#fff" }}>{legOrigin}</span>
        <span style={{ color: statusConfig.color }}>→</span>
        <span className="font-bold" style={{ color: "#fff" }}>{legDestination}</span>
        {route.isBidirectional && <span style={{ color: "#525252" }}>↺</span>}
      </div>

      {/* Distance */}
      <span className="text-xs min-w-[60px]" style={{ color: "#525252" }}>
        {distance}nm
      </span>

      {/* Aircraft */}
      <div className="flex-1 min-w-[120px]">
        {assignedAircraft ? (
          <span className="text-xs">
            <span className="font-mono" style={{ color: "#22d3ee" }}>{assignedAircraft.registration}</span>
            <span className="ml-1" style={{ color: "#525252" }}>{assignedAircraft.model || assignedAircraft.type}</span>
          </span>
        ) : (
          <button
            onClick={() => onAssignAircraft(route.id)}
            className="text-xs px-2 py-0.5 rounded"
            style={{ backgroundColor: "#1a1a1a", color: "#737373", border: "1px dashed #404040" }}
          >
            + Assign
          </button>
        )}
      </div>

      {/* Revenue or time remaining */}
      <div className="text-xs min-w-[60px] text-right">
        {actualStatus === 'in_flight' && timeRemaining ? (
          <span style={{ color: "#3b82f6" }}>{timeRemaining}</span>
        ) : estimatedRevenue > 0 ? (
          <span style={{ color: "#22c55e" }}>{formatMoney(estimatedRevenue)}</span>
        ) : (
          <span style={{ color: "#525252" }}>--</span>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1">
        {actualStatus === 'ready' && (
          <>
            <button
              onClick={() => onLaunch(route)}
              className="text-xs px-2 py-1 rounded font-bold"
              style={{ backgroundColor: "#22c55e", color: "#000" }}
            >
              DEPART
            </button>
            {onSchedule && (
              <button
                onClick={() => onSchedule(route)}
                className="text-xs px-2 py-1 rounded"
                style={{ backgroundColor: "#8b5cf620", color: "#8b5cf6", border: "1px solid #8b5cf6" }}
              >
                SCHED
              </button>
            )}
          </>
        )}
        {actualStatus === 'in_flight' && currentFlight && (
          <button
            onClick={() => onTrack(currentFlight)}
            className="text-xs px-2 py-1 rounded"
            style={{ backgroundColor: "#3b82f620", color: "#3b82f6", border: "1px solid #3b82f6" }}
          >
            TRACK
          </button>
        )}
        {actualStatus === 'turnaround' && (
          <span className="text-xs px-2 py-1" style={{ color: "#f59e0b" }}>Turning</span>
        )}

        {/* Unassign button */}
        {assignedAircraft && actualStatus !== 'in_flight' && (
          <button
            onClick={(e) => { e.stopPropagation(); onUnassign(route.id); }}
            className="text-xs px-1 hover:bg-orange-900/50 rounded"
            title="Unassign"
            style={{ color: "#f59e0b" }}
          >
            x
          </button>
        )}

        {/* Delete button */}
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(route.id); }}
          className="text-xs px-1 hover:bg-red-900/50 rounded"
          title="Delete Route"
          style={{ color: "#ef4444" }}
        >
          x
        </button>
      </div>
    </div>
  );
}

// Main RoutesPanel Component
export default function RoutesPanel({ onNewRoute, onTrackFlight, onSchedule }) {
  const { t } = useTranslation();
  const {
    worldState,
    setWorldState,
    createFlight,
    completeFlight,
    updateRoute,
    assignAircraftToRoute,
    addRoute,
    removeRoute,
    removeScheduledFlight,
  } = useWorldState();

  const activeFlights = useActiveFlights();
  const recentCancellations = useRecentCancellations();
  const routes = worldState?.routes || [];
  const aircraft = worldState?.aircraft || [];
  const crew = worldState?.crew || [];

  // Range warning dialog state (must be before useCallbacks that use it)
  const [rangeWarning, setRangeWarning] = useState(null);

  // Accordion expanded state - default: Ready expanded, others collapsed
  const [expandedSections, setExpandedSections] = useState({
    ready: true,
    in_flight: false,
    unassigned: false,
  });

  // View mode: 'grid' or 'list'
  const [viewMode, setViewMode] = useState('grid');

  // Wings modal state
  const [showWingsModal, setShowWingsModal] = useState(false);
  const wings = getWingsState(worldState);
  const staleRouteCount = useMemo(() => findStaleRoutes(worldState).length, [worldState]);

  // Anomaly event modal (for Spruce Moose, etc.)
  const [anomalyEvent, setAnomalyEvent] = useState(null);
  const [showTheCodeMinigame, setShowTheCodeMinigame] = useState(false);
  const [pendingTheCodeFlight, setPendingTheCodeFlight] = useState(null);
  const theCodeMinigameComplete = useRef(false);

  const toggleSection = useCallback((sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  }, []);

  // Helper to compute route status for grouping (mirrors RouteCard logic)
  const getRouteStatus = useCallback((route) => {
    // Check for active flight first (both directions for round-trip routes)
    const currentFlight = activeFlights.find(f =>
      f.aircraftId === route.aircraftId && (
        f.routeId === route.id ||
        (f.origin === route.origin && f.destination === route.destination) ||
        (f.origin === route.destination && f.destination === route.origin)  // Return leg
      )
    );

    if (currentFlight) {
      const progress = currentFlight.distanceFraction ?? currentFlight.progress ?? 0;
      const phase = currentFlight.phase || '';
      const status = currentFlight.status || '';

      // Only consider done when explicitly marked as arrived/completed/landed
      // Do NOT use progress >= 0.99 - this causes blinking as flights near completion
      const isDone =
        phase === 'arrived' || status === 'arrived' ||
        phase === 'completed' || status === 'completed' ||
        phase === 'landed' || status === 'landed' ||
        currentFlight.awaitingVisualArrival === true;

      if (isDone) return 'ready';
      if (phase === 'turnaround' || status === 'turnaround') return 'turnaround';
      return 'in_flight';
    }

    // No active flight - check if aircraft is assigned
    if (!route.aircraftId) return 'unassigned';

    const assignedAircraft = aircraft?.find(a => a.id === route.aircraftId);
    if (assignedAircraft?.status === 'turnaround') return 'turnaround';
    if (assignedAircraft?.status === 'available' || assignedAircraft?.status === 'ready') return 'ready';
    return route.status || 'ready';
  }, [activeFlights, aircraft]);

  // Group routes by status for accordion display
  // CRITICAL: Deduplicate by ID first to prevent duplicate key rendering bugs
  const groupedRoutes = useMemo(() => {
    const groups = {
      ready: [],
      in_flight: [],
      unassigned: [],
    };

    // Deduplicate routes by ID (keep first occurrence)
    const seenIds = new Set();
    const uniqueRoutes = routes.filter(route => {
      if (seenIds.has(route.id)) {
        console.warn('[RoutesPanel] Duplicate route ID detected, skipping:', route.id);
        return false;
      }
      seenIds.add(route.id);
      return true;
    });

    for (const route of uniqueRoutes) {
      const status = getRouteStatus(route);
      if (groups[status]) {
        groups[status].push(route);
      } else {
        // Fallback for unknown statuses
        groups.ready.push(route);
      }
    }

    return groups;
  }, [routes, getRouteStatus]);

  // Available aircraft - only exclude if IN FLIGHT or has unavailable status
  // Aircraft assigned to routes but not flying ARE available for reassignment
  const availableAircraft = useMemo(() => {
    if (!aircraft || aircraft.length === 0) {
      return [];
    }

    const inFlightIds = new Set(activeFlights.map(f => f.aircraftId));
    const unavailableStatuses = ['delivery', 'maintenance', 'grounded', 'sold', 'museum'];

    return aircraft.filter(a => {
      const isInFlight = inFlightIds.has(a.id);
      const isUnavailable = unavailableStatuses.includes(a.status);
      return !isInFlight && !isUnavailable;
    });
  }, [aircraft, activeFlights]);

  // Count aircraft at ANY hub (for Deploy Idle button)
  // HUB-ONLY DEPARTURES: Aircraft can only depart from hubs
  const playerHubs = worldState?.ownedHubs || [worldState?.airline?.hub || 'KPDX'];
  const playerHubSet = useMemo(() => new Set(playerHubs), [playerHubs]);

  // Aircraft IDs already assigned to routes
  const assignedAircraftIds = useMemo(() => {
    return new Set(routes.map(r => r.aircraftId).filter(Boolean));
  }, [routes]);

  // Get idle aircraft at hub (not already assigned to a route)
  const idleAtHub = useMemo(() => {
    return availableAircraft.filter(ac => {
      if (assignedAircraftIds.has(ac.id)) return false; // Skip already assigned
      const location = ac.location || 'KPDX';
      return playerHubSet.has(location);
    });
  }, [availableAircraft, playerHubSet, assignedAircraftIds]);

  const aircraftAtHubCount = idleAtHub.length;

  // Count unassigned routes (routes without aircraft)
  const unassignedRouteCount = useMemo(() => {
    return routes.filter(r => !r.aircraftId).length;
  }, [routes]);

  // Calculate actual deployable count by simulating deployment
  // Must match the logic in handleDeployIdleFleet - aircraft must be at route origin
  const deployableCount = useMemo(() => {
    const unassignedRoutes = routes.filter(r => !r.aircraftId);
    const usedAircraft = new Set();
    let count = 0;

    for (const route of unassignedRoutes) {
      const legOrigin = route.currentLegOrigin || route.origin;
      const suitable = idleAtHub.find(ac => {
        if (usedAircraft.has(ac.id)) return false;
        return (ac.location || 'KPDX') === legOrigin;
      });
      if (suitable) {
        usedAircraft.add(suitable.id);
        count++;
      }
    }
    return count;
  }, [routes, idleAtHub]);

  // Aircraft NOT at a hub (stranded - can only fly back to hub)
  const strandedAircraftCount = useMemo(() => {
    return availableAircraft.filter(ac => {
      const location = ac.location;
      return location && !playerHubSet.has(location);
    }).length;
  }, [availableAircraft, playerHubSet]);

  // Launch a single route (internal - called after range check passes or user confirms)
  const executeLaunch = useCallback((route, forceRangeExceeded = false) => {
    const ac = aircraft.find(a => a.id === route.aircraftId);
    if (!ac) {
      console.warn('[RoutesPanel] No aircraft assigned to route:', route.id);
      return;
    }

    // Guard against multiple dispatches - check if aircraft is already in flight
    const alreadyInFlight = activeFlights.some(f => f.aircraftId === route.aircraftId);
    if (alreadyInFlight) {
      console.warn('[RoutesPanel] Aircraft already in flight, ignoring dispatch:', route.aircraftId);
      return;
    }

    // Auto-assign crew in Simple mode
    // Note: Roles can be either snake_case (from Supabase raw) or camelCase (after mapping)
    const availableCaptains = crew.filter(c => c.role === 'captain' && (c.status === 'available' || c.dutyStatus === 'available'));
    const availableFOs = crew.filter(c => (c.role === 'firstOfficer' || c.role === 'first_officer') && (c.status === 'available' || c.dutyStatus === 'available'));
    const availableFAs = crew.filter(c => (c.role === 'flightAttendant' || c.role === 'flight_attendant') && (c.status === 'available' || c.dutyStatus === 'available'));

    if (availableCaptains.length === 0 || availableFOs.length === 0) {
      console.warn('[RoutesPanel] No crew available for route:', route.id);
      alert('No crew available! Need at least 1 captain and 1 first officer.');
      return;
    }

    // Calculate flight times - use GAME TIME, not real time
    const gameTime = worldState?.gameTime ? new Date(worldState.gameTime) : new Date();
    const now = gameTime;

    // ROUND-TRIP: Use current leg direction (falls back to base origin/destination for old routes)
    const legOrigin = route.currentLegOrigin || route.origin;
    const legDestination = route.currentLegDestination || route.destination;
    const originAirport = GLOBAL_AIRPORTS[legOrigin];
    const destAirport = GLOBAL_AIRPORTS[legDestination];

    // OTP PRESSURE: Calculate delay if dispatching late
    // If route has a scheduledDeparture and current time is past it, calculate delay
    let dispatchDelayMinutes = 0;
    if (route.scheduledDeparture) {
      const scheduledDep = new Date(route.scheduledDeparture);
      if (now > scheduledDep) {
        dispatchDelayMinutes = Math.floor((now - scheduledDep) / 60000);
        debug('[RoutesPanel] LATE DISPATCH:', {
          route: route.id,
          scheduledDeparture: route.scheduledDeparture,
          currentTime: now.toISOString(),
          delayMinutes: dispatchDelayMinutes,
        });
      }
    }

    // Calculate distance with GLOBAL_AIRPORTS fallback for manual routes
    let distance = calculateDistanceWithGlobal(legOrigin, legDestination);
    if (!Number.isFinite(distance) || distance <= 0) {
      console.error('[RoutesPanel] Invalid distance for route:', {
        origin: legOrigin,
        destination: legDestination,
        distance,
        originFound: !!originAirport,
        destFound: !!destAirport
      });
      alert(`Cannot launch flight: invalid route distance. Check that both ${legOrigin} and ${legDestination} exist.`);
      return;
    }

    const flightTimeHours = calculateFlightTime(distance, ac.type || ac.model);
    const arrivalTime = new Date(now.getTime() + flightTimeHours * 3600000);

    // FUEL CHECK - Calculate fuel needed and check availability
    const aircraftData = AIRCRAFT_DATABASE[ac.type || ac.model] || {};
    const fuelBurnRate = ac.fuelBurn || aircraftData.fuelBurn || 2000; // Default 2000 gal/hr
    const fuelNeeded = calculateFuelNeeded(flightTimeHours, fuelBurnRate);
    const currentFuel = worldState?.fuelReserves || 0;
    const fuelCheck = checkFuelAvailability(currentFuel, fuelNeeded);

    if (!fuelCheck.canFly) {
      const shortageK = Math.ceil(fuelCheck.shortage / 1000);
      alert(`Insufficient fuel! Need ${Math.ceil(fuelNeeded / 1000)}K gallons, have ${Math.ceil(currentFuel / 1000)}K. Short by ${shortageK}K gallons.\n\nVisit the Fuel Depot to purchase more fuel.`);
      return;
    }

    // Deduct fuel from reserves
    setWorldState(prev => ({
      ...prev,
      fuelReserves: Math.max(0, (prev.fuelReserves || 0) - fuelNeeded),
    }));

    debug('[RoutesPanel] FUEL DEDUCTION:', {
      flightRoute: `${legOrigin} → ${legDestination}`,
      flightTimeHours: flightTimeHours.toFixed(2),
      fuelBurnRate,
      fuelNeeded,
      fuelBefore: currentFuel,
      fuelAfter: currentFuel - fuelNeeded,
    });

    // Build crew array
    const crewArray = [
      { id: availableCaptains[0].id, role: 'captain' },
      { id: availableFOs[0].id, role: 'first_officer' },
      ...availableFAs.slice(0, ac.crew?.fa || 2).map(fa => ({ id: fa.id, role: 'flight_attendant' })),
    ];

    // Create flight with all required parameters
    // Simple mode: immediate departure (no scheduling)
    // Simple mode revenue: $10/nm per seat (5x AM4 economy)
    const pax = Math.floor((ac.capacity || 76) * 0.85);
    const ticketPrice = 10 * distance; // $10 per nautical mile
    let revenue = Math.round(pax * ticketPrice);

    // GHOST APOTHEOSIS: $10M bonus revenue per flight (mythic reward)
    if (ac.eosId === 'ghost-apotheosis') {
      revenue += 10000000; // +$10M
      debug('[RoutesPanel] Ghost Apotheosis bonus: +$10M');
    }

    // FUEL CHAOS: Market Maker causes insane cost swings
    const chaos = worldState?.fuelMarketChaos;
    const dayNumber = worldState?.dayNumber || 1;
    let cost;
    if (chaos?.active && dayNumber <= chaos.endDay) {
      // Random fuel price in chaos range ($0.05 to $496.43/gal)
      const chaosPrice = chaos.priceMin + (Math.random() * (chaos.priceMax - chaos.priceMin));
      // Fuel cost = chaos price * estimated gallons (rough: 2 gal/nm)
      const fuelCost = Math.round(chaosPrice * distance * 2);
      cost = fuelCost + 500; // Add base ops cost
      debug(`[RoutesPanel] CHAOS COST: $${chaosPrice.toFixed(2)}/gal → $${cost.toLocaleString()} total`);
    } else {
      cost = Math.round(distance * 1 + 500); // Normal: $1/nm + $500 base
    }
    const profit = revenue - cost;

    // DEBUG: Log all revenue calculation values
    debug('[RoutesPanel] REVENUE CALCULATION:', {
      flightRoute: `${legOrigin} → ${legDestination}`,
      aircraftId: ac.id,
      aircraftType: ac.type || ac.model,
      aircraftCapacity: ac.capacity,
      distance: distance,
      pax: pax,
      ticketPrice: ticketPrice,
      revenue: revenue,
      cost: cost,
      profit: profit,
      formula: `pax(${pax}) x ticketPrice($${ticketPrice}) = $${revenue}`,
    });

    // Get aircraft range from database (reusing aircraftData from fuel check above)
    const aircraftRange = ac.range || aircraftData.range || 2000;

    // =================================================================
    // ANOMALOUS BEHAVIOR CHECK - Process special aircraft behaviors
    // =================================================================
    const currentDay = worldState?.dayNumber || 1;
    const currentHour = now.getHours();

    // Check if aircraft is grounded (e.g., Moonshiner after getting busted)
    if (isAircraftGrounded(ac.eosId || ac.id, currentDay)) {
      const daysRemaining = getGroundedDaysRemaining(ac.eosId || ac.id, currentDay);
      toast({
        title: "Aircraft Grounded",
        description: `This aircraft is grounded for ${daysRemaining} more day(s). It cannot be dispatched.`,
        variant: "destructive",
        duration: 5000,
      });
      // Refund fuel since we're canceling
      setWorldState(prev => ({
        ...prev,
        fuelReserves: (prev.fuelReserves || 0) + fuelNeeded,
      }));
      return;
    }

    // THE CODE - Minigame before flight
    if ((ac.eosId === "the-code" || ac.model === "The Code") && !theCodeMinigameComplete.current) {
      debug("[RoutesPanel] THE CODE detected - storing flight, showing minigame");
      setPendingTheCodeFlight({ route, ac, legOrigin, legDestination, distance, pax, crewArray, fuelNeeded });
      setShowTheCodeMinigame(true);
      return; // Wait for minigame to complete
    }
    // Check for anomalous behavior on dispatch
    let flightOrigin = legOrigin;
    let flightDestination = legDestination;
    let flightAnimation = null;
    let instantComplete = false;
    let anomalousRevenue = null; // Override revenue for anomalous aircraft
    let durationMultiplier = 1.0; // Mobius takes 2x time
    let revenueMultiplier = 1.0; // Mobius earns 2x revenue
    let timeSlipData = null; // Agee-Whells time slip data
    let autoRedispatchData = null; // Echo Flight auto-redispatch
    let perpetualData = null; // The Perpetual eternal flight
    let invisibleOnMap = false; // Blackout - invisible during flight

    // DEBUG: Log for any aircraft with eosId
    if (ac.eosId) {
      debug('[RoutesPanel DEBUG] Aircraft has eosId:', ac.eosId, 'hasAnomalousBehavior:', hasAnomalousBehavior(ac.eosId));
    }

    if (hasAnomalousBehavior(ac.eosId || ac.id)) {
      const gameState = {
        currentDay,
        currentHour,
        allAirports: Object.keys(GLOBAL_AIRPORTS),
        aircraftUses: worldState?.aircraftUses || {},
      };

      const flightData = {
        origin: legOrigin,
        destination: legDestination,
        distance,
      };

      const anomalyResult = processAnomalousBehavior(ac.eosId || ac.id, 'dispatch', flightData, gameState);
      debug('[RoutesPanel DEBUG] anomalyResult for', ac.eosId, ':', anomalyResult);

      // Check if dispatch is blocked
      if (anomalyResult.blocked) {
        toast({
          title: "Dispatch Blocked",
          description: anomalyResult.blockMessage || 'This aircraft cannot be dispatched right now.',
          variant: "destructive",
          duration: 5000,
        });
        // Refund fuel
        setWorldState(prev => ({
          ...prev,
          fuelReserves: (prev.fuelReserves || 0) + fuelNeeded,
        }));
        return;
      }

      // Apply any modifications from anomalous behavior
      if (anomalyResult.flightData) {
        flightOrigin = anomalyResult.flightData.origin || legOrigin;
        flightDestination = anomalyResult.flightData.destination || legDestination;
        flightAnimation = anomalyResult.flightData.animation || null;
        instantComplete = anomalyResult.flightData.instantComplete || false;
        // Use anomalous revenue if set (e.g., Spruce Moose = $0.01)
        if (anomalyResult.flightData.revenue !== undefined) {
          anomalousRevenue = anomalyResult.flightData.revenue;
        }
        // Mobius: 2x duration, 2x revenue
        if (anomalyResult.flightData.durationMultiplier) {
          durationMultiplier = anomalyResult.flightData.durationMultiplier;
        }
        if (anomalyResult.flightData.revenueMultiplier) {
          revenueMultiplier = anomalyResult.flightData.revenueMultiplier;
        }
        // Agee-Whells: Time slip data
        if (anomalyResult.flightData.timeSlipActive) {
          timeSlipData = {
            timeSlipActive: anomalyResult.flightData.timeSlipActive,
            timeSlipDisappearAt: anomalyResult.flightData.timeSlipDisappearAt,
            timeSlipReappearOnDay: anomalyResult.flightData.timeSlipReappearOnDay,
            timeSlipModalDisappear: anomalyResult.flightData.timeSlipModalDisappear,
            timeSlipModalReappear: anomalyResult.flightData.timeSlipModalReappear,
          };
          debug('[RoutesPanel] Time slip data for Agee-Whells:', timeSlipData);
        }
        // Echo Flight: Auto-redispatch data
        if (anomalyResult.flightData.autoRedispatch) {
          autoRedispatchData = {
            autoRedispatch: true,
          };
          debug('[RoutesPanel] Echo Flight: autoRedispatch enabled');
        }
        // Blackout: Invisible on map during flight
        if (anomalyResult.flightData.invisibleOnMap) {
          invisibleOnMap = true;
          debug('[RoutesPanel] Blackout: invisibleOnMap enabled');
        }
        // The Perpetual: Eternal flight data
        if (anomalyResult.flightData.neverAutoComplete) {
          perpetualData = {
            neverAutoComplete: true,
            perpetualStartDay: anomalyResult.flightData.perpetualStartDay,
            equatorWaypoints: anomalyResult.flightData.equatorWaypoints,
            currentWaypointIndex: anomalyResult.flightData.currentWaypointIndex || 0,
            originalOrigin: anomalyResult.flightData.originalOrigin,
            originalDestination: anomalyResult.flightData.originalDestination,
            passiveIncome: anomalyResult.flightData.passiveIncome,
          };
          debug('[RoutesPanel] The Perpetual: neverAutoComplete enabled', perpetualData);
        }
      }

      // Process immediate side effects (modal notifications, fuel chaos, etc.)
      if (anomalyResult.sideEffects && anomalyResult.sideEffects.length > 0) {
        // Check if there's a permanent_ground effect (for museum-style celebration)
        const hasPermanentGround = anomalyResult.sideEffects.some(e => e.type === 'permanent_ground');

        for (const effect of anomalyResult.sideEffects) {
          if (effect.type === 'toast') {
            debug('[Anomalous Event]', effect.message);
            // Show centered anomaly notification instead of corner toast
            // Use museum type if retiring to get confetti celebration
            setTimeout(() => {
              setAnomalyEvent({
                title: ac?.name || 'ANOMALOUS EVENT',
                message: effect.message,
                type: hasPermanentGround ? 'museum' : 'dispatch',
                subtitle: hasPermanentGround ? 'Now in your trophy case forever' : null,
                aircraftId: ac?.id,
              });
            }, 100);
          } else if (effect.type === 'anomaly_modal') {
            // Show ANOMALY DETECTED modal with tagline format
            debug('[Anomalous Event] Showing anomaly modal:', effect.modal);
            setAnomalyEvent({
              title: effect.modal.title || 'ANOMALY DETECTED',
              subtitle: effect.modal.subtitle,
              tagline: effect.modal.tagline,
              message: effect.modal.message,
              type: 'anomaly_dispatch',
              aircraftId: effect.aircraftId,
            });
          } else if (effect.type === 'fuel_market_chaos') {
            // Set temporary fuel price chaos
            setWorldState(prev => ({
              ...prev,
              fuelMarketChaos: {
                active: true,
                endDay: currentDay + effect.durationDays,
                priceMin: effect.priceMin,
                priceMax: effect.priceMax,
              },
            }));
            debug('[RoutesPanel] Fuel market chaos activated:', effect);
          } else if (effect.type === 'permanent_ground') {
            // Immediately retire aircraft to museum (Yesterday, etc.)
            debug('[RoutesPanel] Permanent ground on dispatch:', effect);
            setWorldState(prev => ({
              ...prev,
              aircraft: prev.aircraft.map(a =>
                (a.id === ac.id || a.eosId === effect.aircraftId)
                  ? { ...a, status: 'museum', museumSince: new Date().toISOString() }
                  : a
              ),
            }));
            // Unassign from route
            assignAircraftToRoute(route.id, null);
            updateRoute(route.id, { status: 'unassigned' });
          } else if (effect.type === 'trigger_minigame') {
            // The Code - trigger 8-bit bug squashing minigame
            debug('[RoutesPanel] Triggering minigame:', effect.minigameType);
            setShowTheCodeMinigame(true);
          }
        }
      }

      // Check if we should skip creating the flight (Yesterday - already "flew")
      if (anomalyResult.flightData?.skipFlight) {
        debug('[RoutesPanel] Skipping flight creation for:', ac.eosId);
        return; // Don't create the flight - it already "happened"
      }
    }

    // Use anomalous revenue if set, otherwise apply revenue multiplier
    const multipliedRevenue = Math.round(revenue * revenueMultiplier);
    const finalRevenue = anomalousRevenue !== null ? anomalousRevenue : multipliedRevenue;
    const finalProfit = anomalousRevenue !== null ? (anomalousRevenue - cost) : (multipliedRevenue - cost);

    // Recalculate arrival time with duration multiplier (Mobius takes 2x time)
    const adjustedFlightTimeHours = flightTimeHours * durationMultiplier;
    const adjustedArrivalTime = new Date(now.getTime() + adjustedFlightTimeHours * 3600000);

    if (durationMultiplier !== 1.0 || revenueMultiplier !== 1.0) {
      debug('[RoutesPanel] ANOMALOUS MULTIPLIERS:', {
        aircraft: ac.name || ac.id,
        durationMultiplier,
        revenueMultiplier,
        originalFlightTime: flightTimeHours.toFixed(2) + 'h',
        adjustedFlightTime: adjustedFlightTimeHours.toFixed(2) + 'h',
        originalRevenue: revenue,
        multipliedRevenue,
      });
    }

    // DEBUG: Log flight creation with route leg info
    debug(`[FLIGHT LAUNCH] Creating flight for route ${route.id}:`, {
      routeBaseOrigin: route.origin,
      routeBaseDestination: route.destination,
      routeCurrentLegOrigin: route.currentLegOrigin,
      routeCurrentLegDestination: route.currentLegDestination,
      computedLegOrigin: legOrigin,
      computedLegDestination: legDestination,
      finalFlightOrigin: flightOrigin,
      finalFlightDestination: flightDestination,
      routeStatus: route.status,
    });

    const result = createFlight({
      origin: flightOrigin,
      destination: flightDestination,
      aircraftId: route.aircraftId,
      aircraftType: ac.type || ac.model || 'E175',
      crew: crewArray,
      passengers: pax,
      scheduledDeparture: now.toISOString(),
      scheduledArrival: adjustedArrivalTime.toISOString(), // Uses duration multiplier for Mobius
      estimatedRevenue: finalRevenue,
      estimatedCost: cost,
      routeId: route.id,
      // SIMPLE: Immediate takeoff - skip scheduling phase
      immediateDepart: true,
      // FUEL ENFORCEMENT: Flag if range exceeded
      rangeExceeded: forceRangeExceeded,
      aircraftRange: forceRangeExceeded ? aircraftRange : undefined,
      // OTP PRESSURE: Pass dispatch delay to flight
      totalDelayMinutes: dispatchDelayMinutes,
      // Pre-calculate earnings to bypass GameLoopEngine's complex formula
      preCalculatedEarnings: {
        revenue: finalRevenue,
        costs: cost,
        profit: finalProfit,
        passengers: pax,
        loadFactor: 0.85,
        distance,
        blockHours: adjustedFlightTimeHours, // Uses duration multiplier for Mobius
      },
      // ANOMALOUS: Special animation and behavior properties
      animation: flightAnimation,
      eosId: ac.eosId || null,
      instantComplete: instantComplete,
      // AGEE-WHELLS: Time slip data (disappear after 10s, reappear 2 days later)
      ...(timeSlipData || {}),
      // ECHO FLIGHT: Auto-redispatch on arrival
      ...(autoRedispatchData || {}),
      // THE PERPETUAL: Eternal equator circumnavigation
      ...(perpetualData || {}),
      // BLACKOUT: Invisible on map during flight
      invisibleOnMap: invisibleOnMap,
    });

    // Check for errors (slot validation, etc.)
    if (result?.error) {
      console.error('[RoutesPanel] Flight creation failed:', result.message);
      alert(result.message || 'Failed to create flight');
      return;
    }

    if (result?.id) {
      updateRoute(route.id, {
        status: 'in_flight',
        lastFlightId: result.id,
        totalFlights: (route.totalFlights || 0) + 1,
        // OTP PRESSURE: Clear scheduled departure after dispatch
        // New schedule will be set after turnaround
        scheduledDeparture: null,
        groundPhase: null,
        groundPhaseStartedAt: null,
        dispatchedAt: now.toISOString(),
      });
      debug('[RoutesPanel] Flight launched:', result.id, {
        rangeExceeded: forceRangeExceeded,
        aircraftRange: forceRangeExceeded ? aircraftRange : 'N/A',
        distance: distance,
        dispatchDelay: dispatchDelayMinutes,
        flightData: result,
        instantComplete: instantComplete,
      });

      // ANOMALOUS: Handle instant-complete flights (Spruce Moose, Yesterday)
      if (instantComplete && completeFlight) {
        debug('[RoutesPanel] INSTANT COMPLETE triggered for:', result.id, 'revenue:', finalRevenue);

        // Process 'complete' event to get side effects (toasts, etc.)
        const completeResult = processAnomalousBehavior(ac.eosId || ac.id, 'complete', {
          ...result,
          baseRevenue: finalRevenue,
        }, { currentDay, currentHour });

        let shouldPermanentGround = false;

        // Show celebratory modal for anomalous events (instead of boring toast)
        if (completeResult.sideEffects) {
          let anomalyMessage = null;
          for (const effect of completeResult.sideEffects) {
            if (effect.type === 'toast') {
              anomalyMessage = effect.message;
            } else if (effect.type === 'permanent_ground') {
              shouldPermanentGround = true;
            }
          }
          // Show the modal with confetti instead of toast
          if (anomalyMessage) {
            setAnomalyEvent({
              title: ac?.name || 'ANOMALOUS EVENT',
              message: anomalyMessage,
              type: shouldPermanentGround ? 'museum' : 'complete',
              subtitle: shouldPermanentGround ? 'Now in your trophy case forever' : null,
              aircraftId: ac?.id,
            });
          }
        }

        // Complete immediately with the anomalous earnings
        setTimeout(() => {
          completeFlight(result.id, {
            revenue: finalRevenue,
            costs: cost,
            profit: finalProfit,
          });

          if (shouldPermanentGround) {
            // Unassign aircraft from route - it's now a museum piece
            assignAircraftToRoute(route.id, null);
            updateRoute(route.id, {
              status: 'unassigned',
              lastCompletedAt: new Date().toISOString(),
              totalRevenue: (route.totalRevenue || 0) + finalProfit,
            });
            // Update aircraft status to museum/retired
            setWorldState(prev => ({
              ...prev,
              aircraft: prev.aircraft.map(a =>
                a.id === ac.id
                  ? { ...a, status: 'museum', museumSince: new Date().toISOString() }
                  : a
              ),
            }));
            debug('[RoutesPanel] Aircraft permanently grounded (museum piece):', ac.id);
          } else {
            // Update route status back to ready
            updateRoute(route.id, {
              status: 'ready',
              lastCompletedAt: new Date().toISOString(),
              totalRevenue: (route.totalRevenue || 0) + finalProfit,
            });
          }
        }, 100); // Small delay so toast can show
      }
    }
  }, [aircraft, crew, worldState?.gameTime, worldState?.fuelReserves, activeFlights, createFlight, updateRoute, setWorldState, completeFlight, assignAircraftToRoute]);

  // Launch a single route (with range check)
  const handleLaunch = useCallback((route) => {
    const ac = aircraft.find(a => a.id === route.aircraftId);
    if (!ac) {
      console.warn('[RoutesPanel] No aircraft assigned to route:', route.id);
      return;
    }

    // ROUND-TRIP: Use current leg direction
    const legOrigin = route.currentLegOrigin || route.origin;
    const legDestination = route.currentLegDestination || route.destination;

    // Calculate distance with GLOBAL_AIRPORTS fallback
    const distance = calculateDistanceWithGlobal(legOrigin, legDestination);
    if (!Number.isFinite(distance) || distance <= 0) {
      alert(`Cannot launch flight: invalid route distance.`);
      return;
    }

    // Get aircraft range from database
    const aircraftData = AIRCRAFT_DATABASE[ac.type || ac.model] || {};
    const aircraftRange = ac.range || aircraftData.range || 2000;

    // Check if range is exceeded
    debug('[RoutesPanel] Range check:', { distance, aircraftRange, exceeded: distance > aircraftRange, acType: ac.type || ac.model });
    if (distance > aircraftRange) {
      debug('[RoutesPanel] RANGE EXCEEDED - showing warning dialog');
      // Show warning dialog instead of launching
      setRangeWarning({
        route,
        aircraft: ac,
        distance,
        range: aircraftRange,
        origin: legOrigin,
        destination: legDestination,
      });
      return;
    }

    // Range OK - launch normally
    executeLaunch(route, false);
  }, [aircraft, executeLaunch]);

  // Handle "Proceed Anyway" from range warning dialog
  const handleProceedWithRangeExceeded = useCallback(() => {
    if (!rangeWarning) return;
    executeLaunch(rangeWarning.route, true);
    setRangeWarning(null);
  }, [rangeWarning, executeLaunch]);

  // Deploy Idle Fleet - assign idle aircraft to existing unassigned routes
  // Does NOT create new routes, only assigns aircraft to routes that need them
  const handleDeployIdleFleet = useCallback(() => {
    // Get unassigned routes (routes without aircraft)
    const unassignedRoutes = routes.filter(r => !r.aircraftId);
    if (unassignedRoutes.length === 0) return;

    // Get idle aircraft at hub, not already assigned
    const idleAtHub = availableAircraft.filter(ac => {
      if (assignedAircraftIds.has(ac.id)) return false;
      const location = ac.location || 'KPDX';
      return playerHubSet.has(location);
    });
    if (idleAtHub.length === 0) return;

    let assigned = 0;
    const usedAircraft = new Set();

    // Assign aircraft to unassigned routes
    for (const route of unassignedRoutes) {
      // Find an available aircraft at the route's current leg origin
      const legOrigin = route.currentLegOrigin || route.origin;
      const suitable = idleAtHub.find(ac => {
        if (usedAircraft.has(ac.id)) return false;
        const acLocation = ac.location || 'KPDX';
        return acLocation === legOrigin;
      });

      if (suitable) {
        assignAircraftToRoute(route.id, suitable.id);
        usedAircraft.add(suitable.id);
        assigned++;
      }
    }

    if (assigned > 0) {
      debug(`[Deploy Idle] Assigned ${assigned} aircraft to routes`);
    }
  }, [availableAircraft, routes, assignAircraftToRoute, playerHubSet, assignedAircraftIds]);

  // Assign aircraft modal state
  const [assigningRoute, setAssigningRoute] = useState(null);
  // Delete confirmation state (native confirm() breaks Electron input focus)
  const [pendingDeleteRoute, setPendingDeleteRoute] = useState(null);

  const handleAssignAircraft = useCallback((routeId) => {
    setAssigningRoute(routeId);
  }, []);

  const confirmAssignment = useCallback((aircraftId) => {
    if (assigningRoute) {
      // First, unassign this aircraft from any other route it might be on
      const existingRoute = routes.find(r => r.aircraftId === aircraftId && r.id !== assigningRoute);
      if (existingRoute) {
        assignAircraftToRoute(existingRoute.id, null);
      }
      // Now assign to the new route
      assignAircraftToRoute(assigningRoute, aircraftId);
      setAssigningRoute(null);
    }
  }, [assigningRoute, assignAircraftToRoute, routes]);

  // Delete a route — uses React modal instead of native confirm() which breaks Electron input focus
  const handleDeleteRoute = useCallback((routeId) => {
    const route = routes.find(r => r.id === routeId);
    if (!route) return;

    // Check if aircraft is in flight on this route
    const inFlight = activeFlights.some(f => f.routeId === routeId);
    if (inFlight) {
      toast({ title: 'Cannot delete route', description: 'Aircraft is in flight. Wait for landing or cancel the flight first.', duration: 4000 });
      return;
    }

    setPendingDeleteRoute(route);
  }, [routes, activeFlights, toast]);

  const confirmDeleteRoute = useCallback(() => {
    if (pendingDeleteRoute) {
      removeRoute(pendingDeleteRoute.id);
      debug('[RoutesPanel] Route deleted:', pendingDeleteRoute.id);
      setPendingDeleteRoute(null);
    }
  }, [pendingDeleteRoute, removeRoute]);

  // Unassign aircraft from a route
  const handleUnassignAircraft = useCallback((routeId) => {
    const route = routes.find(r => r.id === routeId);
    if (!route) return;

    // Check if aircraft is in flight on this route
    const inFlight = activeFlights.some(f => f.routeId === routeId);
    if (inFlight) {
      alert('Cannot unassign aircraft while it is in flight.');
      return;
    }

    // Unassign by setting aircraftId to null
    assignAircraftToRoute(routeId, null);
    debug('[RoutesPanel] Aircraft unassigned from route:', routeId);
  }, [routes, activeFlights, assignAircraftToRoute]);

  // Count ready routes for Launch All button
  const readyRouteCount = (groupedRoutes.ready || []).length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold" style={{ color: "#fff" }}>
          {t('routes.title')}
        </h2>
        <span className="text-sm" style={{ color: "#525252" }}>
          {routes.length !== 1 ? t('routes.routeCountPlural', { count: routes.length }) : t('routes.routeCount', { count: routes.length })}
        </span>
      </div>

      {/* Action bar */}
      <div className="flex items-center gap-2 p-3 rounded" style={{ backgroundColor: "#0c0c0c", border: "1px solid #1a1a1a" }}>
        <Button onClick={onNewRoute} style={{ backgroundColor: "#22c55e", color: "#000" }}>
          {t('routes.newRoute')}
        </Button>

        <Button
          onClick={() => {
            const readyRoutes = groupedRoutes.ready || [];
            readyRoutes.forEach(route => handleLaunch(route));
          }}
          disabled={readyRouteCount === 0}
          style={{
            backgroundColor: readyRouteCount > 0 ? "#22c55e" : "#1a1a1a",
            color: readyRouteCount > 0 ? "#000" : "#525252",
            fontWeight: 'bold',
          }}
        >
          {t('routes.launchAll')} ({readyRouteCount})
        </Button>

        <Button
          onClick={handleDeployIdleFleet}
          disabled={deployableCount === 0}
          style={{
            backgroundColor: deployableCount > 0 ? "#3b82f6" : "#1a1a1a",
            color: deployableCount > 0 ? "#fff" : "#525252",
          }}
          title={deployableCount === 0 && unassignedRouteCount === 0 ? 'No unassigned routes' :
                 deployableCount === 0 && aircraftAtHubCount === 0 ? 'No idle aircraft at hub' : ''}
        >
          {t('routes.deployIdle')} ({deployableCount})
        </Button>

        {/* Wings - Auto Route Generation */}
        <Button
          onClick={() => setShowWingsModal(true)}
          style={{
            backgroundColor: "#1a0f2e",
            color: "#a78bfa",
            border: "1px solid #8b5cf640",
          }}
          title={`Spend Wings to auto-generate routes${staleRouteCount > 0 ? ` (${staleRouteCount} stale)` : ''}`}
        >
          {t('routes.wings')} ({wings.balance || 0}){staleRouteCount > 0 && <span style={{ color: "#ef4444", marginLeft: "4px" }}>!</span>}
        </Button>

        {/* View mode toggle */}
        <div className="flex rounded overflow-hidden ml-auto" style={{ border: "1px solid #333" }}>
          <button
            onClick={() => setViewMode('grid')}
            className="px-2 py-1 text-xs transition-colors"
            style={{
              backgroundColor: viewMode === 'grid' ? '#22c55e20' : 'transparent',
              color: viewMode === 'grid' ? '#22c55e' : '#525252',
            }}
            title="Grid view"
          >
            {t('routes.grid')}
          </button>
          <button
            onClick={() => setViewMode('list')}
            className="px-2 py-1 text-xs transition-colors"
            style={{
              backgroundColor: viewMode === 'list' ? '#22c55e20' : 'transparent',
              color: viewMode === 'list' ? '#22c55e' : '#525252',
              borderLeft: '1px solid #333',
            }}
            title="List view"
          >
            {t('routes.list')}
          </button>
        </div>
      </div>

      {/* Routes accordion - grouped by status */}
      {routes.length === 0 ? (
        <div className="text-center py-12" style={{ color: "#737373" }}>
          <p className="text-4xl opacity-30 mb-4">--</p>
          <p>{t('routes.noRoutes')}</p>
          <p className="text-sm mt-2">{t('routes.noRoutesHint')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {ACCORDION_SECTIONS.map(section => {
            const sectionRoutes = groupedRoutes[section.key] || [];
            return (
              <AccordionSection
                key={section.key}
                label={t(`routes.${section.labelKey}`)}
                color={section.color}
                count={sectionRoutes.length}
                isExpanded={expandedSections[section.key]}
                onToggle={() => toggleSection(section.key)}
              >
                {viewMode === 'grid' ? (
                  <div
                    className="grid gap-2"
                    style={{
                      gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                    }}
                  >
                    {sectionRoutes.map(route => (
                      <RouteCard
                        key={route.id}
                        route={route}
                        aircraft={aircraft}
                        activeFlights={activeFlights}
                        recentCancellations={recentCancellations}
                        gameTime={worldState?.gameTime}
                        routeDemand={worldState?.routeDemand}
                        onLaunch={handleLaunch}
                        onAssignAircraft={handleAssignAircraft}
                        onTrack={(flight) => onTrackFlight?.(flight)}
                        onSchedule={onSchedule}
                        onDelete={handleDeleteRoute}
                        onUnassign={handleUnassignAircraft}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-1">
                    {sectionRoutes.map(route => (
                      <RouteListItem
                        key={route.id}
                        route={route}
                        aircraft={aircraft}
                        activeFlights={activeFlights}
                        recentCancellations={recentCancellations}
                        gameTime={worldState?.gameTime}
                        routeDemand={worldState?.routeDemand}
                        onLaunch={handleLaunch}
                        onAssignAircraft={handleAssignAircraft}
                        onTrack={(flight) => onTrackFlight?.(flight)}
                        onSchedule={onSchedule}
                        onDelete={handleDeleteRoute}
                        onUnassign={handleUnassignAircraft}
                      />
                    ))}
                  </div>
                )}
              </AccordionSection>
            );
          })}
        </div>
      )}


      {/* Scheduled Flights Panel */}
      {(worldState?.scheduledFlights || []).filter(s => s.enabled).length > 0 && (
        <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: "#0a0a0a", border: "1px solid #1a1a1a" }}>
          <h3 className="text-sm font-bold mb-2" style={{ color: "#8b5cf6" }}>
            Scheduled Flights ({(worldState?.scheduledFlights || []).filter(s => s.enabled).length})
          </h3>
          <div className="space-y-1">
            {(worldState?.scheduledFlights || []).filter(s => s.enabled).map(sched => {
              const route = routes.find(r => r.id === sched.routeId);
              const origin = route?.currentLegOrigin || route?.origin || '???';
              const dest = route?.currentLegDestination || route?.destination || '???';

              // Check if schedule time has passed today
              const gameTime = worldState?.gameTime ? new Date(worldState.gameTime) : new Date();
              const [schedH, schedM] = sched.departureTime.split(':').map(Number);
              const schedTotalMin = schedH * 60 + schedM;
              const nowTotalMin = gameTime.getHours() * 60 + gameTime.getMinutes();
              const hasFiredToday = sched.lastDispatchedDay || nowTotalMin >= schedTotalMin;

              // Only show flight status if this schedule has actually dispatched
              let statusLabel = null;
              let statusColor = '#525252';
              if (hasFiredToday) {
                const activeFlight = activeFlights.find(f => f.routeId === sched.routeId);
                if (activeFlight) {
                  const phase = activeFlight.phase || activeFlight.status;
                  if (phase === 'cruise' || phase === 'climb') {
                    statusLabel = 'IN FLIGHT';
                    statusColor = '#3b82f6';
                  } else if (phase === 'descent' || phase === 'approach' || phase === 'landing') {
                    statusLabel = 'LANDING';
                    statusColor = '#f59e0b';
                  } else if (phase === 'takeoff' || phase === 'taxi_out' || phase === 'pushback') {
                    statusLabel = 'DEPARTED';
                    statusColor = '#22c55e';
                  } else {
                    statusLabel = phase?.toUpperCase();
                    statusColor = '#3b82f6';
                  }
                } else {
                  statusLabel = 'DONE';
                  statusColor = '#525252';
                }
              } else {
                statusLabel = 'WAITING';
                statusColor = '#525252';
              }

              return (
                <div key={sched.id} className="flex items-center justify-between py-1 px-2 rounded"
                  style={{ backgroundColor: "#111", border: "1px solid #222" }}>
                  <div className="flex items-center gap-3 text-xs">
                    <span style={{ color: "#22c55e", fontWeight: "bold", fontFamily: "monospace" }}>
                      {sched.departureTime}
                    </span>
                    <span style={{ color: "#fff" }}>{origin} → {dest}</span>
                    {sched.recurring && <span className="px-1 rounded" style={{ backgroundColor: "#8b5cf620", color: "#8b5cf6", fontSize: "10px" }}>DAILY</span>}
                    {sched.autoReturn && <span className="px-1 rounded" style={{ backgroundColor: "#f59e0b20", color: "#f59e0b", fontSize: "10px" }}>RETURN</span>}
                    {statusLabel && <span className="px-1 rounded" style={{ backgroundColor: `${statusColor}20`, color: statusColor, fontSize: "10px", fontWeight: "bold" }}>{statusLabel}</span>}
                    <span style={{ color: "#525252" }}>
                      {sched.aircraftId === 'next-available' ? 'Next avail.' : (aircraft.find(a => a.id === sched.aircraftId)?.registration || '?')}
                    </span>
                  </div>
                  <button
                    onClick={() => removeScheduledFlight(sched.id)}
                    className="text-xs px-1 hover:bg-red-900/50 rounded"
                    style={{ color: "#ef4444" }}
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Aircraft Assignment Modal */}
      {assigningRoute && (() => {
        // Look up the actual route object from the ID
        const route = routes.find(r => r.id === assigningRoute);
        if (!route) return null;

        // ROUND-TRIP: Use current leg origin for aircraft filtering
        const legOrigin = route.currentLegOrigin || route.origin;
        const legDestination = route.currentLegDestination || route.destination;

        // Filter aircraft by location - only show aircraft AT the current leg's origin or at any hub
        const aircraftAtOrigin = availableAircraft.filter(ac =>
          ac.location === legOrigin ||
          playerHubSet.has(ac.location) // Also allow aircraft at any player hub
        );
        const aircraftElsewhere = availableAircraft.filter(ac =>
          ac.location !== legOrigin &&
          !playerHubSet.has(ac.location)
        );

        return (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
            <div className="bg-zinc-900 rounded-lg p-6 max-w-md w-full mx-4" style={{ border: "1px solid #333" }}>
              <h3 className="text-lg font-bold mb-2" style={{ color: "#fff" }}>Assign Aircraft</h3>
              <p className="text-sm mb-4" style={{ color: "#737373" }}>
                Route: {legOrigin} → {legDestination} {route.isBidirectional ? '↺' : ''}
              </p>

              {aircraftAtOrigin.length === 0 ? (
                <div>
                  <p style={{ color: "#f59e0b" }}>
                    No aircraft at {legOrigin}.
                  </p>
                  {aircraftElsewhere.length > 0 && (
                    <p className="mt-2 text-sm" style={{ color: "#737373" }}>
                      {aircraftElsewhere.length} aircraft available elsewhere:
                      {aircraftElsewhere.slice(0, 3).map(ac => (
                        <span key={ac.id} className="ml-2">
                          {ac.registration} @ {ac.location}
                        </span>
                      ))}
                    </p>
                  )}
                  {availableAircraft.length === 0 && (
                    <p style={{ color: "#737373" }}>All aircraft are assigned or in flight.</p>
                  )}
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {aircraftAtOrigin.map(ac => {
                    // Check if this aircraft is assigned to another route
                    const assignedRoute = routes.find(r => r.aircraftId === ac.id);
                    const isAssignedElsewhere = assignedRoute && assignedRoute.id !== assigningRoute;

                    return (
                      <button
                        key={ac.id}
                        onClick={() => confirmAssignment(ac.id)}
                        className="w-full p-3 rounded text-left hover:bg-zinc-800"
                        style={{ backgroundColor: "#1a1a1a", border: `1px solid ${isAssignedElsewhere ? '#f59e0b40' : '#333'}` }}
                      >
                        <div className="flex justify-between items-center">
                          <span>
                            <span className="font-mono" style={{ color: "#22d3ee" }}>{ac.registration}</span>
                            <span className="ml-2" style={{ color: "#a1a1a1" }}>{ac.model || ac.type}</span>
                          </span>
                          <span style={{ color: "#737373" }}>{ac.capacity || 76} pax</span>
                        </div>
                        {isAssignedElsewhere && (
                          <div className="text-xs mt-1" style={{ color: "#f59e0b" }}>
                            Currently on: {assignedRoute.origin}→{assignedRoute.destination} (will reassign)
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="mt-4 flex justify-end">
                <Button variant="outline" onClick={() => setAssigningRoute(null)}>Cancel</Button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Range Warning Modal - YELLOW warning for insufficient range */}
      {rangeWarning && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div
            className="bg-zinc-900 rounded-lg p-6 max-w-lg w-full mx-4"
            style={{ border: "2px solid #f59e0b" }}
          >
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center"
                style={{ backgroundColor: "#f59e0b20" }}
              >
                <span style={{ color: "#f59e0b", fontSize: "24px", fontWeight: "bold" }}>!</span>
              </div>
              <div>
                <h3 className="text-xl font-bold" style={{ color: "#f59e0b" }}>
                  RANGE WARNING
                </h3>
                <p className="text-sm" style={{ color: "#a1a1a1" }}>
                  Insufficient fuel for destination
                </p>
              </div>
            </div>

            {/* Route info */}
            <div
              className="p-4 rounded-lg mb-4"
              style={{ backgroundColor: "#1a1a1a", border: "1px solid #333" }}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-2xl font-bold" style={{ color: "#fff" }}>
                  {rangeWarning.origin}
                </span>
                <span style={{ color: "#f59e0b", fontWeight: "bold" }}>&gt;</span>
                <span className="text-2xl font-bold" style={{ color: "#fff" }}>
                  {rangeWarning.destination}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span style={{ color: "#737373" }}>Aircraft Range:</span>
                  <span className="ml-2 font-bold" style={{ color: "#22c55e" }}>
                    {rangeWarning.range.toLocaleString()} nm
                  </span>
                </div>
                <div>
                  <span style={{ color: "#737373" }}>Route Distance:</span>
                  <span className="ml-2 font-bold" style={{ color: "#ef4444" }}>
                    {rangeWarning.distance.toLocaleString()} nm
                  </span>
                </div>
              </div>

              <div className="mt-3 pt-3" style={{ borderTop: "1px solid #333" }}>
                <span style={{ color: "#737373" }}>Shortfall:</span>
                <span className="ml-2 font-bold" style={{ color: "#ef4444" }}>
                  {(rangeWarning.distance - rangeWarning.range).toLocaleString()} nm
                </span>
              </div>
            </div>

            {/* Warning message */}
            <div
              className="p-3 rounded-lg mb-4 flex items-start gap-2"
              style={{ backgroundColor: "#1f0a0a", border: "1px solid #ef4444" }}
            >
              <span className="flex-shrink-0 mt-0.5 font-bold" style={{ color: "#ef4444" }}>!!</span>
              <div className="text-sm" style={{ color: "#fca5a5" }}>
                <strong>This aircraft CANNOT reach the destination.</strong>
                <br />
                If you proceed, the aircraft WILL run out of fuel mid-flight.
                <br />
                <span style={{ color: "#f59e0b" }}>
                  - Within 100nm of airport: Emergency divert (fines + reputation loss)
                </span>
                <br />
                <span style={{ color: "#ef4444" }}>
                  - Beyond 100nm: Crash landing (severe fines + major reputation loss)
                </span>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setRangeWarning(null)}
                style={{ borderColor: "#525252", color: "#a1a1a1" }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleProceedWithRangeExceeded}
                style={{ backgroundColor: "#ef4444", color: "#fff" }}
              >
                Proceed Anyway
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Route Confirmation Modal */}
      {pendingDeleteRoute && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-zinc-900 rounded-lg p-6 max-w-sm w-full mx-4" style={{ border: "1px solid #ef4444" }}>
            <h3 className="text-lg font-bold mb-2" style={{ color: "#fff" }}>Delete Route?</h3>
            <p className="text-sm mb-4" style={{ color: "#a1a1a1" }}>
              {pendingDeleteRoute.origin} - {pendingDeleteRoute.destination}
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setPendingDeleteRoute(null)}
                style={{ borderColor: "#525252", color: "#a1a1a1" }}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDeleteRoute}
                style={{ backgroundColor: "#ef4444", color: "#fff" }}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Anomaly Event Modal - Celebratory popup for anomalous aircraft */}
      <AnomalyModal
        anomaly={anomalyEvent}
        onClose={() => setAnomalyEvent(null)}
      />

      {/* Wings Spend Modal - Auto route generation */}
      <WingsSpendModal
        isOpen={showWingsModal}
        onClose={() => setShowWingsModal(false)}
      />
      {/* The Code Minigame - 8-bit bug squashing game */}
      {showTheCodeMinigame && (
        <TheCodeMinigame
          onComplete={(score) => {
            debug("[TheCodeMinigame] Complete! Score:", score);
            setShowTheCodeMinigame(false);
            theCodeMinigameComplete.current = true;
            toast({
              title: "BUILD SUCCESSFUL",
              description: `Score: ${score} - 0 errors, 0 warnings`,
              duration: 3000,
            });
            // Resume the flight
            if (pendingTheCodeFlight) {
              setTimeout(() => {
                handleLaunch(pendingTheCodeFlight.route);
                setPendingTheCodeFlight(null);
                theCodeMinigameComplete.current = false;
              }, 500);
            }
          }}
          onFail={() => {
            debug("[TheCodeMinigame] Failed!");
            setShowTheCodeMinigame(false);
            setPendingTheCodeFlight(null);
            toast({
              title: "RUNTIME ERROR",
              description: "Flight cancelled - too many bugs!",
              variant: "destructive",
              duration: 5000,
            });
          }}
        />
      )}
    </div>
  );
}
